// EmployeesPage.js
// Employee Management page with tabs for Employees, Payroll, Settings, and Metrics

import React, { useState, useMemo } from 'react';
import { useIsPhone } from '../hooks/useIsMobile';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Checkbox,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PaymentsIcon from '@mui/icons-material/Payments';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

// Chart imports
import 'chart.js/auto';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../contexts/UserContext';
import ResponsiveToolbar from '../components/ResponsiveToolbar';
import {
  getEmployees,
  getDepartments,
  getEmployeeTypes,
  getPayrollSubmissions,
  getEmployeeMetrics,
  deleteDepartment,
  deleteEmployeeType,
  deleteEmployee,
  deletePayrollSubmission,
  bulkUpdatePayrollStatus,
} from '../services/employeeService';

// Import dialogs
import EmployeeCreateDialog from './dialogs/EmployeeCreateDialog';
import EmployeeEditDialog from './dialogs/EmployeeEditDialog';
import DepartmentDialog from './dialogs/DepartmentDialog';
import EmployeeTypeDialog from './dialogs/EmployeeTypeDialog';
import PayrollSubmissionDialog from './dialogs/PayrollSubmissionDialog';
import BulkPayrollDialog from './dialogs/BulkPayrollDialog';
import PaymentDateDialog from './dialogs/PaymentDateDialog';
import ConfirmDeleteDialog from './dialogs/ConfirmDeleteDialog';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`employees-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Summary Card Component
function SummaryCard({ title, value, color = 'text.primary', suffix = '' }) {
  return (
    <Card sx={{ minWidth: 140 }}>
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ color, fontWeight: 'bold', fontSize: '1.5rem' }}>
          {value}{suffix}
        </Typography>
      </CardContent>
    </Card>
  );
}

// Chart color palette
const CHART_COLORS = [
  '#1976d2', // Primary blue
  '#2e7d32', // Success green
  '#ed6c02', // Warning orange
  '#9c27b0', // Purple
  '#00bcd4', // Cyan
  '#f44336', // Red
  '#ff9800', // Amber
  '#607d8b', // Blue grey
];

// Short Hebrew month names for charts
const SHORT_MONTH_NAMES = ['', 'ינו׳', 'פבר׳', 'מרץ', 'אפר׳', 'מאי', 'יוני', 'יולי', 'אוג׳', 'ספט׳', 'אוק׳', 'נוב׳', 'דצמ׳'];

const EmployeesPage = () => {
  const queryClient = useQueryClient();
  const { userProfile } = useUser();
  const isPhone = useIsPhone();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Tab state - persist in URL
  const activeTab = parseInt(searchParams.get('tab') || '0', 10);
  const setActiveTab = (newTab) => {
    setSearchParams({ tab: newTab.toString() }, { replace: true });
  };

  // Employee filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Payroll filters
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [payrollYear, setPayrollYear] = useState(currentYear);
  const [payrollMonth, setPayrollMonth] = useState(currentMonth);

  // Waiting section filters
  const [waitingSearchQuery, setWaitingSearchQuery] = useState('');
  const [waitingDeptFilter, setWaitingDeptFilter] = useState('');
  const [waitingStatusFilter, setWaitingStatusFilter] = useState('active');

  // Submitted payroll table filters
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState('');
  const [submittedDeptFilter, setSubmittedDeptFilter] = useState('');
  const [submittedStatusFilter, setSubmittedStatusFilter] = useState('');

  // Bulk payment status
  const [selectedSubmissions, setSelectedSubmissions] = useState({});
  const [showPaymentDateDialog, setShowPaymentDateDialog] = useState(false);

  // Metrics filters
  const [metricsYear, setMetricsYear] = useState(currentYear);
  const [metricsMonth, setMetricsMonth] = useState(currentMonth);

  // Dialog states
  const [showCreateEmployeeDialog, setShowCreateEmployeeDialog] = useState(false);
  const [showEditEmployeeDialog, setShowEditEmployeeDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [showDepartmentDialog, setShowDepartmentDialog] = useState(false);
  const [editDepartment, setEditDepartment] = useState(null);

  const [showEmployeeTypeDialog, setShowEmployeeTypeDialog] = useState(false);
  const [editEmployeeType, setEditEmployeeType] = useState(null);

  const [showPayrollDialog, setShowPayrollDialog] = useState(false);
  const [selectedEmployeeForPayroll, setSelectedEmployeeForPayroll] = useState(null);
  const [showBulkPayrollDialog, setShowBulkPayrollDialog] = useState(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type, id, name }

  // Fetch employees (needed for both Employees tab and Payroll tab)
  const { data: employees = [], isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['employees', statusFilter, departmentFilter, typeFilter, searchQuery],
    queryFn: () => getEmployees({
      status: statusFilter || undefined,
      department_id: departmentFilter || undefined,
      type_id: typeFilter || undefined,
      search: searchQuery || undefined,
    }),
    enabled: activeTab === 1 || activeTab === 2,
    staleTime: 30 * 1000,
  });

  // Fetch departments
  const { data: departments = [], isLoading: isLoadingDepartments } = useQuery({
    queryKey: ['departments'],
    queryFn: getDepartments,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch employee types
  const { data: employeeTypes = [], isLoading: isLoadingTypes } = useQuery({
    queryKey: ['employee-types'],
    queryFn: getEmployeeTypes,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch payroll submissions
  const { data: payrollData, isLoading: isLoadingPayroll } = useQuery({
    queryKey: ['payroll-submissions', payrollYear, payrollMonth],
    queryFn: () => getPayrollSubmissions({ year: payrollYear, month: payrollMonth }),
    enabled: activeTab === 2,
    staleTime: 30 * 1000,
  });

  // Fetch metrics
  const { data: metricsData, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ['employeeMetrics', metricsYear, metricsMonth],
    queryFn: () => getEmployeeMetrics({ year: metricsYear, month: metricsMonth }),
    enabled: activeTab === 0,
    staleTime: 60 * 1000,
  });

  // Handlers
  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowEditEmployeeDialog(true);
  };

  const handleDeleteEmployee = (employee) => {
    setDeleteTarget({ type: 'employee', id: employee.EmployeeID, name: employee.FullName });
    setShowDeleteDialog(true);
  };

  const handleEditDepartment = (dept) => {
    setEditDepartment(dept);
    setShowDepartmentDialog(true);
  };

  const handleDeleteDepartment = (dept) => {
    // Count employees in this department
    const employeeCount = employees.filter(emp =>
      emp.DepartmentIDs?.includes(dept.DepartmentID)
    ).length;
    setDeleteTarget({ type: 'department', id: dept.DepartmentID, name: dept.Name, employeeCount });
    setShowDeleteDialog(true);
  };

  const handleEditEmployeeType = (type) => {
    setEditEmployeeType(type);
    setShowEmployeeTypeDialog(true);
  };

  const handleDeleteEmployeeType = (type) => {
    setDeleteTarget({ type: 'employeeType', id: type.TypeID, name: type.Name });
    setShowDeleteDialog(true);
  };

  const handleSubmitPayroll = (employee) => {
    setSelectedEmployeeForPayroll(employee);
    setShowPayrollDialog(true);
  };

  const handleDeletePayroll = (submission) => {
    setDeleteTarget({
      type: 'payroll',
      id: submission.SubmissionID,
      name: `${submission.EmployeeName} - ${submission.PeriodDisplay}`
    });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      switch (deleteTarget.type) {
        case 'employee':
          await deleteEmployee(deleteTarget.id);
          queryClient.invalidateQueries({ queryKey: ['employees'] });
          break;
        case 'department':
          await deleteDepartment(deleteTarget.id);
          queryClient.invalidateQueries({ queryKey: ['departments'] });
          break;
        case 'employeeType':
          await deleteEmployeeType(deleteTarget.id);
          queryClient.invalidateQueries({ queryKey: ['employee-types'] });
          break;
        case 'payroll':
          await deletePayrollSubmission(deleteTarget.id);
          queryClient.invalidateQueries({ queryKey: ['payroll-submissions'] });
          queryClient.invalidateQueries({ queryKey: ['cashflow'] });
          break;
        default:
          break;
      }
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    } catch (err) {
      throw err;
    }
  };

  // Bulk payment status mutation
  const bulkStatusMutation = useMutation({
    mutationFn: bulkUpdatePayrollStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['cashflow'] });
      setSelectedSubmissions({});
      setShowPaymentDateDialog(false);
    },
  });

  // Handler for single "mark as paid"
  const handleMarkAsPaid = (submission) => {
    setSelectedSubmissions({ [submission.SubmissionID]: true });
    setShowPaymentDateDialog(true);
  };

  // Handler for toggling submission selection
  const handleToggleSubmission = (submissionId) => {
    setSelectedSubmissions(prev => ({
      ...prev,
      [submissionId]: !prev[submissionId],
    }));
  };

  // Handler for select all waiting submissions
  const handleSelectAllWaiting = (event) => {
    if (event.target.checked) {
      const newSelected = {};
      filteredSubmittedPayroll.forEach(s => {
        if (s.PaymentStatus === 'waiting') {
          newSelected[s.SubmissionID] = true;
        }
      });
      setSelectedSubmissions(newSelected);
    } else {
      setSelectedSubmissions({});
    }
  };

  // Get selected submission IDs
  const selectedSubmissionIds = useMemo(() => {
    return Object.entries(selectedSubmissions).filter(([, v]) => v).map(([k]) => parseInt(k));
  }, [selectedSubmissions]);

  // Get total of selected submissions
  const selectedTotal = useMemo(() => {
    if (!payrollData?.items) return 0;
    return payrollData.items
      .filter(s => selectedSubmissions[s.SubmissionID])
      .reduce((sum, s) => sum + (s.TotalAmount || 0), 0);
  }, [payrollData, selectedSubmissions]);

  // Get all employees for payroll waiting section (includes all statuses)
  const allEmployeesForPayroll = useMemo(() => {
    return employees;
  }, [employees]);

  // Get active employees for payroll
  const activeEmployees = useMemo(() => {
    return employees.filter(e => e.Status === 'active');
  }, [employees]);

  // Get employees without payroll for current period
  const employeesWithoutPayroll = useMemo(() => {
    if (!payrollData?.items) return allEmployeesForPayroll;
    const submittedIds = payrollData.items.map(p => p.Employee);
    return allEmployeesForPayroll.filter(e => !submittedIds.includes(e.EmployeeID));
  }, [allEmployeesForPayroll, payrollData]);

  // Filtered waiting employees (with search, department, status filters)
  const filteredWaitingEmployees = useMemo(() => {
    let list = employeesWithoutPayroll;
    if (waitingSearchQuery) {
      const q = waitingSearchQuery.toLowerCase();
      list = list.filter(e =>
        e.FullName?.toLowerCase().includes(q) ||
        e.FirstName?.toLowerCase().includes(q) ||
        e.LastName?.toLowerCase().includes(q)
      );
    }
    if (waitingDeptFilter) {
      list = list.filter(e =>
        e.DepartmentIDs?.includes(parseInt(waitingDeptFilter)) ||
        e.DepartmentNames?.some(name => name === departments.find(d => d.DepartmentID === parseInt(waitingDeptFilter))?.Name)
      );
    }
    if (waitingStatusFilter) {
      list = list.filter(e => e.Status === waitingStatusFilter);
    }
    return list;
  }, [employeesWithoutPayroll, waitingSearchQuery, waitingDeptFilter, waitingStatusFilter, departments]);

  // Filtered submitted payroll (client-side filters on top of server-side payment_status filter)
  const filteredSubmittedPayroll = useMemo(() => {
    let list = payrollData?.items || [];
    if (submittedSearchQuery) {
      const q = submittedSearchQuery.toLowerCase();
      list = list.filter(s => s.EmployeeName?.toLowerCase().includes(q));
    }
    if (submittedDeptFilter) {
      const deptName = departments.find(d => d.DepartmentID === parseInt(submittedDeptFilter))?.Name;
      list = list.filter(s => s.EmployeeDepartments?.includes(deptName));
    }
    if (submittedStatusFilter) {
      list = list.filter(s => s.PaymentStatus === submittedStatusFilter);
    }
    return list;
  }, [payrollData, submittedSearchQuery, submittedDeptFilter, submittedStatusFilter, departments]);

  // Month names in Hebrew
  const monthNames = ['', 'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
    'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

  // Status chip colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'terminated': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
          ניהול עובדים
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, newVal) => setActiveTab(newVal)}
          variant={isPhone ? 'scrollable' : 'fullWidth'}
          scrollButtons={isPhone ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minWidth: { xs: 'auto', sm: 90 },
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.85rem', sm: '1rem' },
            },
          }}
        >
          <Tab label="מדדים" />
          <Tab label="עובדים" />
          <Tab label="משכורות" />
          <Tab label="הגדרות" />
        </Tabs>

        {/* Tab 1: Employees */}
        <TabPanel value={activeTab} index={1}>
          {/* Filters */}
          <ResponsiveToolbar>
            <TextField
              size="small"
              placeholder="חיפוש..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: { xs: 0, md: 200 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 120 } }}>
              <InputLabel>סטטוס</InputLabel>
              <Select
                value={statusFilter}
                label="סטטוס"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">הכל</MenuItem>
                <MenuItem value="active">פעיל</MenuItem>
                <MenuItem value="inactive">לא פעיל</MenuItem>
                <MenuItem value="terminated">סיים עבודה</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 150 } }}>
              <InputLabel>מחלקה</InputLabel>
              <Select
                value={departmentFilter}
                label="מחלקה"
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <MenuItem value="">הכל</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.DepartmentID} value={dept.DepartmentID}>
                    {dept.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 150 } }}>
              <InputLabel>סוג עובד</InputLabel>
              <Select
                value={typeFilter}
                label="סוג עובד"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="">הכל</MenuItem>
                {employeeTypes.map((type) => (
                  <MenuItem key={type.TypeID} value={type.TypeID}>
                    {type.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateEmployeeDialog(true)}
              sx={{ direction: 'ltr' }}
            >
              עובד חדש
            </Button>
          </ResponsiveToolbar>

          {/* Employees Table */}
          {isLoadingEmployees ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : employees.length === 0 ? (
            <Alert severity="info">לא נמצאו עובדים</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>שם</TableCell>
                    <TableCell>ת.ז.</TableCell>
                    <TableCell>טלפון</TableCell>
                    <TableCell>מחלקות</TableCell>
                    <TableCell>סוג</TableCell>
                    <TableCell>שכר</TableCell>
                    <TableCell>סטטוס</TableCell>
                    <TableCell>פעולות</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow key={employee.EmployeeID} hover>
                      <TableCell>
                        <Typography
                          fontWeight="medium"
                          color="primary"
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onClick={() => navigate(`/employees/${employee.EmployeeID}`)}
                        >
                          {employee.FullName}
                        </Typography>
                        {employee.EmployeeNumber && (
                          <Typography variant="body2" color="text.secondary">
                            #{employee.EmployeeNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{employee.IDNumber || '-'}</TableCell>
                      <TableCell>{employee.PhoneNumber || '-'}</TableCell>
                      <TableCell>
                        {employee.DepartmentNames?.length > 0
                          ? employee.DepartmentNames.join(', ')
                          : '-'}
                      </TableCell>
                      <TableCell>{employee.EmployeeTypeName || '-'}</TableCell>
                      <TableCell>
                        <Typography>
                          ₪{parseFloat(employee.WageAmount).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {employee.WageTypeDisplay}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={employee.StatusDisplay}
                          color={getStatusColor(employee.Status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ direction: 'ltr' }}>
                        <Tooltip title="עריכה">
                          <IconButton size="small" onClick={() => handleEditEmployee(employee)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="מחיקה">
                          <IconButton size="small" onClick={() => handleDeleteEmployee(employee)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab 2: Payroll */}
        <TabPanel value={activeTab} index={2}>
          {/* Period selector */}
          <ResponsiveToolbar sx={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 120 } }}>
              <InputLabel>שנה</InputLabel>
              <Select
                value={payrollYear}
                label="שנה"
                onChange={(e) => setPayrollYear(e.target.value)}
              >
                {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 120 } }}>
              <InputLabel>חודש</InputLabel>
              <Select
                value={payrollMonth}
                label="חודש"
                onChange={(e) => setPayrollMonth(e.target.value)}
              >
                {monthNames.slice(1).map((name, idx) => (
                  <MenuItem key={idx + 1} value={idx + 1}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="outlined"
              startIcon={<PaymentsIcon />}
              onClick={() => setShowBulkPayrollDialog(true)}
              disabled={employeesWithoutPayroll.length === 0}
              sx={{ direction: 'ltr' }}
            >
              הגשה מרוכזת ({employeesWithoutPayroll.length})
            </Button>
          </ResponsiveToolbar>

          {/* Summary cards */}
          {payrollData && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item>
                <SummaryCard
                  title="סה״כ משכורות"
                  value={`₪${(payrollData.summary?.total_payroll || 0).toLocaleString()}`}
                  color="primary.main"
                />
              </Grid>
              <Grid item>
                <SummaryCard
                  title="ממתינים לתשלום"
                  value={`${payrollData.summary?.waiting_count || 0} (₪${(payrollData.summary?.waiting_total || 0).toLocaleString()})`}
                  color="warning.main"
                />
              </Grid>
              <Grid item>
                <SummaryCard
                  title="שולמו"
                  value={`${payrollData.summary?.paid_count || 0} (₪${(payrollData.summary?.paid_total || 0).toLocaleString()})`}
                  color="success.main"
                />
              </Grid>
              <Grid item>
                <SummaryCard
                  title="טרם הוגשו"
                  value={employeesWithoutPayroll.length}
                  color={employeesWithoutPayroll.length > 0 ? 'error.main' : 'success.main'}
                />
              </Grid>
            </Grid>
          )}

          {/* Payroll table */}
          {isLoadingPayroll ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Employees without payroll */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                  עובדים ממתינים להגשה ({filteredWaitingEmployees.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {employeesWithoutPayroll.length > 0 ? (
                  <>
                    <ResponsiveToolbar sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="חיפוש לפי שם..."
                        value={waitingSearchQuery}
                        onChange={(e) => setWaitingSearchQuery(e.target.value)}
                        sx={{ minWidth: { xs: 0, md: 180 } }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: { xs: 0, md: 130 } }}>
                        <InputLabel>מחלקה</InputLabel>
                        <Select
                          value={waitingDeptFilter}
                          label="מחלקה"
                          onChange={(e) => setWaitingDeptFilter(e.target.value)}
                        >
                          <MenuItem value="">הכל</MenuItem>
                          {departments.map((dept) => (
                            <MenuItem key={dept.DepartmentID} value={dept.DepartmentID}>{dept.Name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: { xs: 0, md: 120 } }}>
                        <InputLabel>סטטוס</InputLabel>
                        <Select
                          value={waitingStatusFilter}
                          label="סטטוס"
                          onChange={(e) => setWaitingStatusFilter(e.target.value)}
                        >
                          <MenuItem value="">הכל</MenuItem>
                          <MenuItem value="active">פעיל</MenuItem>
                          <MenuItem value="inactive">לא פעיל</MenuItem>
                          <MenuItem value="terminated">סיים עבודה</MenuItem>
                        </Select>
                      </FormControl>
                    </ResponsiveToolbar>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>שם</TableCell>
                            <TableCell>מחלקות</TableCell>
                            <TableCell>סוג שכר</TableCell>
                            <TableCell>סכום בסיס</TableCell>
                            <TableCell>פעולות</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredWaitingEmployees.map((employee) => (
                            <TableRow key={employee.EmployeeID} hover>
                              <TableCell>{employee.FullName}</TableCell>
                              <TableCell>{employee.DepartmentNames?.join(', ') || '-'}</TableCell>
                              <TableCell>{employee.WageTypeDisplay}</TableCell>
                              <TableCell>₪{parseFloat(employee.WageAmount).toLocaleString()}</TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleSubmitPayroll(employee)}
                                >
                                  הגש משכורת
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Alert severity="success" sx={{ mt: 1 }}>כל העובדים היגישו משכורת לתקופה זו</Alert>
                )}
              </Box>

              {/* Separator between sections */}
              {payrollData?.items?.length > 0 && (
                <Divider sx={{ my: 3 }} />
              )}

              {/* Submitted payroll */}
              {payrollData?.items?.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      משכורות שהוגשו ({filteredSubmittedPayroll.length})
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {selectedSubmissionIds.length > 0 && (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => setShowPaymentDateDialog(true)}
                        sx={{ direction: 'ltr' }}
                      >
                        סמן כשולם ({selectedSubmissionIds.length})
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="חיפוש לפי שם..."
                      value={submittedSearchQuery}
                      onChange={(e) => { setSubmittedSearchQuery(e.target.value); setSelectedSubmissions({}); }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 180 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>מחלקה</InputLabel>
                      <Select
                        value={submittedDeptFilter}
                        label="מחלקה"
                        onChange={(e) => { setSubmittedDeptFilter(e.target.value); setSelectedSubmissions({}); }}
                      >
                        <MenuItem value="">הכל</MenuItem>
                        {departments.map((dept) => (
                          <MenuItem key={dept.DepartmentID} value={dept.DepartmentID}>{dept.Name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>סטטוס תשלום</InputLabel>
                      <Select
                        value={submittedStatusFilter}
                        label="סטטוס תשלום"
                        onChange={(e) => { setSubmittedStatusFilter(e.target.value); setSelectedSubmissions({}); }}
                      >
                        <MenuItem value="">הכל</MenuItem>
                        <MenuItem value="waiting">ממתין</MenuItem>
                        <MenuItem value="paid">שולם</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={
                                filteredSubmittedPayroll.filter(s => s.PaymentStatus === 'waiting').length > 0 &&
                                filteredSubmittedPayroll.filter(s => s.PaymentStatus === 'waiting').every(s => selectedSubmissions[s.SubmissionID])
                              }
                              indeterminate={
                                selectedSubmissionIds.length > 0 &&
                                !filteredSubmittedPayroll.filter(s => s.PaymentStatus === 'waiting').every(s => selectedSubmissions[s.SubmissionID])
                              }
                              onChange={handleSelectAllWaiting}
                              disabled={filteredSubmittedPayroll.filter(s => s.PaymentStatus === 'waiting').length === 0}
                            />
                          </TableCell>
                          <TableCell>שם עובד</TableCell>
                          <TableCell>סוג שכר</TableCell>
                          <TableCell>שעות/ימים</TableCell>
                          <TableCell>סכום בסיס</TableCell>
                          <TableCell>ברוטו</TableCell>
                          <TableCell>ניכויים</TableCell>
                          <TableCell>תוספות</TableCell>
                          <TableCell>סה״כ</TableCell>
                          <TableCell>סטטוס</TableCell>
                          <TableCell>פעולות</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSubmittedPayroll.map((submission) => (
                          <TableRow key={submission.SubmissionID} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={!!selectedSubmissions[submission.SubmissionID]}
                                onChange={() => handleToggleSubmission(submission.SubmissionID)}
                                disabled={submission.PaymentStatus === 'paid'}
                              />
                            </TableCell>
                            <TableCell>{submission.EmployeeName}</TableCell>
                            <TableCell>{submission.WageTypeDisplay}</TableCell>
                            <TableCell>
                              {submission.WageType === 'hourly' || submission.WageType === 'daily'
                                ? submission.HoursWorked
                                : '-'}
                            </TableCell>
                            <TableCell>₪{parseFloat(submission.BaseAmount || 0).toLocaleString()}</TableCell>
                            <TableCell>₪{parseFloat(submission.GrossAmount || 0).toLocaleString()}</TableCell>
                            <TableCell sx={{ color: 'error.main' }}>
                              {submission.Deductions > 0 ? `-₪${parseFloat(submission.Deductions).toLocaleString()}` : '-'}
                            </TableCell>
                            <TableCell sx={{ color: 'success.main' }}>
                              {submission.Additions > 0 ? `+₪${parseFloat(submission.Additions).toLocaleString()}` : '-'}
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="bold">
                                ₪{parseFloat(submission.TotalAmount || 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={submission.PaymentStatusDisplay}
                                color={submission.PaymentStatus === 'paid' ? 'success' : 'warning'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}>
                              {submission.PaymentStatus === 'waiting' && (
                                <Tooltip title="סמן כשולם">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMarkAsPaid(submission)}
                                    color="success"
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="מחיקה">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeletePayroll(submission)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {payrollData?.items?.length === 0 && employeesWithoutPayroll.length === 0 && (
                <Alert severity="info">אין עובדים פעילים</Alert>
              )}
            </>
          )}
        </TabPanel>

        {/* Tab 3: Settings (Departments & Types) */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            {/* Departments */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">מחלקות</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditDepartment(null);
                    setShowDepartmentDialog(true);
                  }}
                  sx={{ direction: 'ltr' }}
                >
                  מחלקה חדשה
                </Button>
              </Box>

              {isLoadingDepartments ? (
                <CircularProgress />
              ) : departments.length === 0 ? (
                <Alert severity="info">לא נוצרו מחלקות</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>שם</TableCell>
                        <TableCell>תיאור</TableCell>
                        <TableCell>פעולות</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {departments.map((dept) => (
                        <TableRow key={dept.DepartmentID} hover>
                          <TableCell>{dept.Name}</TableCell>
                          <TableCell>{dept.Description || '-'}</TableCell>
                          <TableCell sx={{ direction: 'ltr' }}>
                            <IconButton size="small" onClick={() => handleEditDepartment(dept)} color="primary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteDepartment(dept)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>

            {/* Employee Types */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">סוגי עובדים</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditEmployeeType(null);
                    setShowEmployeeTypeDialog(true);
                  }}
                  sx={{ direction: 'ltr' }}
                >
                  סוג חדש
                </Button>
              </Box>

              {isLoadingTypes ? (
                <CircularProgress />
              ) : employeeTypes.length === 0 ? (
                <Alert severity="info">לא נוצרו סוגי עובדים</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>שם</TableCell>
                        <TableCell>תיאור</TableCell>
                        <TableCell>פעולות</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {employeeTypes.map((type) => (
                        <TableRow key={type.TypeID} hover>
                          <TableCell>{type.Name}</TableCell>
                          <TableCell>{type.Description || '-'}</TableCell>
                          <TableCell sx={{ direction: 'ltr' }}>
                            <IconButton size="small" onClick={() => handleEditEmployeeType(type)} color="primary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteEmployeeType(type)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 0: Metrics */}
        <TabPanel value={activeTab} index={0}>
          {isLoadingMetrics ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : metricsData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* ===== PERIOD SELECTOR AT TOP ===== */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>שנה</InputLabel>
                  <Select
                    value={metricsYear}
                    label="שנה"
                    onChange={(e) => setMetricsYear(e.target.value)}
                  >
                    {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((year) => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>חודש</InputLabel>
                  <Select
                    value={metricsMonth}
                    label="חודש"
                    onChange={(e) => setMetricsMonth(e.target.value)}
                  >
                    {monthNames.slice(1).map((name, idx) => (
                      <MenuItem key={idx + 1} value={idx + 1}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* ===== QUICK INSIGHT BAR ===== */}
              <Paper
                sx={{
                  p: 2,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: 'white',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" align="center" sx={{ fontWeight: 500 }}>
                  יש לך {metricsData.totals?.active_employees || 0} עובדים פעילים
                  {metricsData.department_counts?.length > 0 && ` ב-${metricsData.department_counts.length} מחלקות`}
                  {metricsData.selected_month?.total > 0 && (
                    <> | משכורות {monthNames[metricsMonth]}: ₪{parseFloat(metricsData.selected_month.total || 0).toLocaleString()}</>
                  )}
                </Typography>
              </Paper>

              {/* ===== ENHANCED SUMMARY CARDS WITH ICONS ===== */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <PeopleIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {metricsData.totals?.total_employees || 0}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">סה״כ עובדים</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                        {metricsData.totals?.active_employees || 0}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">עובדים פעילים</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <PaymentsIcon sx={{ fontSize: 40, color: '#ed6c02', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ed6c02' }}>
                        ₪{((metricsData.selected_month?.total || 0) / 1000).toFixed(0)}K
                      </Typography>
                      <Typography variant="body1" color="text.secondary">משכורות {monthNames[metricsMonth]}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{
                    bgcolor: (metricsData.selected_month?.change_from_prev || 0) >= 0 ? '#e8f5e9' : '#ffebee',
                    height: '100%'
                  }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      {(metricsData.selected_month?.change_from_prev || 0) >= 0 ? (
                        <TrendingUpIcon sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 40, color: '#d32f2f', mb: 1 }} />
                      )}
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 'bold',
                          color: (metricsData.selected_month?.change_from_prev || 0) >= 0 ? '#2e7d32' : '#d32f2f'
                        }}
                      >
                        {(metricsData.selected_month?.change_from_prev || 0) >= 0 ? '+' : ''}
                        ₪{(metricsData.selected_month?.change_from_prev || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">שינוי מחודש קודם</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* ===== YEARLY METRICS SECTION ===== */}
              <Typography variant="h6" sx={{ mt: 1 }}>מדדים שנתיים - {metricsYear}</Typography>
              <Grid container spacing={3}>
                {/* Row 1: Monthly Payroll Trend + Payroll by Department - Side by Side */}
                {metricsData.payroll_by_month?.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>מגמת משכורות - {metricsYear}</Typography>
                      <Box sx={{ height: 280 }}>
                        <Bar
                          data={{
                            labels: metricsData.payroll_by_month.map(m => SHORT_MONTH_NAMES[m.PeriodMonth]),
                            datasets: [{
                              label: 'משכורות',
                              data: metricsData.payroll_by_month.map(m => m.total),
                              backgroundColor: metricsData.payroll_by_month.map(m =>
                                m.PeriodMonth === metricsMonth ? '#1976d2' : '#90caf9'
                              ),
                              borderRadius: 4,
                              borderSkipped: false,
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            onClick: (event, elements) => {
                              if (elements.length > 0) {
                                const idx = elements[0].index;
                                setMetricsMonth(metricsData.payroll_by_month[idx].PeriodMonth);
                              }
                            },
                            plugins: {
                              legend: { display: false },
                              tooltip: {
                                callbacks: {
                                  label: (ctx) => `₪${ctx.raw?.toLocaleString() || 0}`
                                }
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  callback: (val) => `₪${(val / 1000).toFixed(0)}K`
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                        לחץ על עמודה לצפייה בפרטי החודש
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {metricsData.payroll_by_department?.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>משכורות לפי מחלקה - {metricsYear}</Typography>
                      <Box sx={{ height: 280 }}>
                        <Bar
                          data={{
                            labels: metricsData.payroll_by_department.map(d => d.Employee__Departments__Name),
                            datasets: [{
                              label: 'סה״כ',
                              data: metricsData.payroll_by_department.map(d => d.total),
                              backgroundColor: CHART_COLORS.slice(0, metricsData.payroll_by_department.length),
                              borderRadius: 4,
                              borderSkipped: false,
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { display: false },
                              tooltip: {
                                callbacks: {
                                  label: (ctx) => `₪${ctx.raw?.toLocaleString() || 0}`
                                }
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  callback: (val) => `₪${(val / 1000).toFixed(0)}K`
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {/* Row 2: Employee Distribution Charts - Side by Side */}
                {metricsData.department_counts?.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>עובדים לפי מחלקה</Typography>
                      <Box sx={{ height: 250, display: 'flex', justifyContent: 'center' }}>
                        <Doughnut
                          data={{
                            labels: metricsData.department_counts.map(d => d.Departments__Name),
                            datasets: [{
                              data: metricsData.department_counts.map(d => d.count),
                              backgroundColor: CHART_COLORS.slice(0, metricsData.department_counts.length),
                              borderWidth: 2,
                              borderColor: '#fff',
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'bottom',
                                rtl: true,
                                labels: { usePointStyle: true, padding: 15 }
                              }
                            }
                          }}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {metricsData.type_counts?.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>עובדים לפי סוג</Typography>
                      <Box sx={{ height: 250, display: 'flex', justifyContent: 'center' }}>
                        <Doughnut
                          data={{
                            labels: metricsData.type_counts.map(t => t.EmployeeType__Name),
                            datasets: [{
                              data: metricsData.type_counts.map(t => t.count),
                              backgroundColor: CHART_COLORS.slice(0, metricsData.type_counts.length),
                              borderWidth: 2,
                              borderColor: '#fff',
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'bottom',
                                rtl: true,
                                labels: { usePointStyle: true, padding: 15 }
                              }
                            }
                          }}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              {/* ===== SELECTED MONTH DETAILS ===== */}
              <Typography variant="h6" sx={{ mt: 1 }}>פירוט חודשי - {monthNames[metricsMonth]} {metricsYear}</Typography>
              {metricsData.selected_month && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={3}>
                    {/* By Department */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                        לפי מחלקה
                      </Typography>
                      {metricsData.selected_month.by_department?.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>מחלקה</TableCell>
                              <TableCell align="right">סה״כ</TableCell>
                              <TableCell align="right">עובדים</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {metricsData.selected_month.by_department.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{item.Employee__Departments__Name}</TableCell>
                                <TableCell align="right">₪{parseFloat(item.total || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{item.count}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography color="text.secondary">אין נתונים</Typography>
                      )}
                    </Grid>

                    {/* By Type */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                        לפי סוג עובד
                      </Typography>
                      {metricsData.selected_month.by_type?.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>סוג</TableCell>
                              <TableCell align="right">סה״כ</TableCell>
                              <TableCell align="right">עובדים</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {metricsData.selected_month.by_type.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{item.Employee__EmployeeType__Name}</TableCell>
                                <TableCell align="right">₪{parseFloat(item.total || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{item.count}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography color="text.secondary">אין נתונים</Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* ===== YEARLY SUMMARY ===== */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">סיכום שנתי - {metricsYear}</Typography>
                  <Typography variant="h6" color="primary.main">
                    סה״כ: ₪{parseFloat(metricsData.total_payroll_year || 0).toLocaleString()}
                  </Typography>
                </Box>
                {metricsData.payroll_by_month?.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>חודש</TableCell>
                        <TableCell align="right">סה״כ</TableCell>
                        <TableCell align="right">עובדים</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metricsData.payroll_by_month.map((item, idx) => (
                        <TableRow
                          key={idx}
                          sx={{
                            bgcolor: item.PeriodMonth === metricsMonth ? 'action.selected' : 'inherit',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                          onClick={() => setMetricsMonth(item.PeriodMonth)}
                        >
                          <TableCell>{monthNames[item.PeriodMonth]}</TableCell>
                          <TableCell align="right">₪{parseFloat(item.total || 0).toLocaleString()}</TableCell>
                          <TableCell align="right">{item.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Box>
          ) : (
            <Alert severity="info">אין נתונים להצגה</Alert>
          )}
        </TabPanel>
      </Paper>

      {/* Dialogs */}
      <EmployeeCreateDialog
        open={showCreateEmployeeDialog}
        onClose={() => setShowCreateEmployeeDialog(false)}
        departments={departments}
        employeeTypes={employeeTypes}
      />

      <EmployeeEditDialog
        open={showEditEmployeeDialog}
        onClose={() => {
          setShowEditEmployeeDialog(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
        departments={departments}
        employeeTypes={employeeTypes}
      />

      <DepartmentDialog
        open={showDepartmentDialog}
        onClose={() => {
          setShowDepartmentDialog(false);
          setEditDepartment(null);
        }}
        department={editDepartment}
      />

      <EmployeeTypeDialog
        open={showEmployeeTypeDialog}
        onClose={() => {
          setShowEmployeeTypeDialog(false);
          setEditEmployeeType(null);
        }}
        employeeType={editEmployeeType}
      />

      <PayrollSubmissionDialog
        open={showPayrollDialog}
        onClose={() => {
          setShowPayrollDialog(false);
          setSelectedEmployeeForPayroll(null);
        }}
        employee={selectedEmployeeForPayroll}
        year={payrollYear}
        month={payrollMonth}
      />

      <BulkPayrollDialog
        open={showBulkPayrollDialog}
        onClose={() => setShowBulkPayrollDialog(false)}
        employees={employeesWithoutPayroll}
        year={payrollYear}
        month={payrollMonth}
      />

      <PaymentDateDialog
        open={showPaymentDateDialog}
        onClose={() => {
          setShowPaymentDateDialog(false);
          setSelectedSubmissions({});
        }}
        submissionIds={selectedSubmissionIds}
        totalAmount={selectedTotal}
        count={selectedSubmissionIds.length}
        isLoading={bulkStatusMutation.isPending}
        onConfirm={(paymentDate) => {
          bulkStatusMutation.mutate({
            SubmissionIDs: selectedSubmissionIds,
            PaymentStatus: 'paid',
            PaymentDate: paymentDate,
          });
        }}
      />

      <ConfirmDeleteDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        title="אישור מחיקה"
        message={
          deleteTarget?.type === 'department' && deleteTarget?.employeeCount > 0
            ? `מחלקה "${deleteTarget?.name}" מכילה ${deleteTarget?.employeeCount} עובדים. המחיקה תסיר את השיוך שלהם למחלקה זו. האם להמשיך?`
            : `האם למחוק את "${deleteTarget?.name}"?`
        }
        confirmButtonText="מחיקה"
        requirePassword={false}
      />
    </Box>
  );
};

export default EmployeesPage;
